/**
 * LongOperationOverlay — Batch #14.49
 *
 * Overlay plein écran avec spinner + message rassurant + timer live.
 * À utiliser pour TOUTE opération qui peut durer > 5s (et particulièrement sur mobile/LTE).
 *
 * Usage :
 *   const [busy, setBusy] = useState(false);
 *   ...
 *   <LongOperationOverlay
 *     visible={busy}
 *     title="Signature en cours…"
 *     message="Nous archivons votre signature et préparons les copies signées."
 *     hint="Cela peut prendre jusqu'à 60 secondes sur réseau mobile."
 *   />
 *
 * Le composant est volontairement bloquant (z-50, fixed inset-0) pour empêcher
 * l'utilisateur de cliquer ailleurs et casser la requête en cours.
 */
import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";

export default function LongOperationOverlay({
  visible,
  title = "Traitement en cours…",
  message = "Veuillez patienter pendant que nous traitons votre demande.",
  hint = "Cela peut prendre jusqu'à 60 secondes sur réseau mobile.",
  testId = "long-operation-overlay",
}) {
  const [seconds, setSeconds] = useState(0);

  useEffect(() => {
    if (!visible) {
      setSeconds(0);
      return;
    }
    const interval = setInterval(() => setSeconds((s) => s + 1), 1000);
    return () => clearInterval(interval);
  }, [visible]);

  if (!visible) return null;

  return (
    <div
      className="fixed inset-0 z-[60] bg-slate-900/80 backdrop-blur-sm flex items-center justify-center p-6"
      data-testid={testId}
    >
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 space-y-4">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0">
            <Loader2 className="w-6 h-6 animate-spin text-emerald-700" />
          </div>
          <h3 className="text-base font-bold text-slate-800">{title}</h3>
        </div>
        <p className="text-sm text-slate-600 leading-relaxed">{message}</p>
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
          <p className="text-[12px] text-amber-900 leading-relaxed">
            <strong>⚠️ Ne fermez pas cette fenêtre.</strong> {hint}
          </p>
        </div>
        <div className="border-t border-slate-100 pt-3 flex items-center justify-between text-[11px]">
          <span className="text-slate-500 font-mono">
            ⏱ Temps écoulé : <strong className="text-slate-800">{seconds}s</strong>
          </span>
          {seconds > 60 && (
            <span className="text-rose-700 font-semibold">
              Le serveur est lent — patientez encore quelques instants…
            </span>
          )}
        </div>
      </div>
    </div>
  );
}
